import { type FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import { useAuth } from '../../contexts/AuthContext'
import { useLang } from '../../contexts/LangContext'
import { SIGN_UP_MUTATION } from '../../graphql/auth'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import FormError from '../../components/ui/FormError'
import type { CurrentUser } from '../../contexts/AuthContext'

interface SignUpData {
  signUp: {
    user: CurrentUser | null
    token: string | null
    errors: string[]
  }
}

interface Fields {
  firstName: string
  lastName: string
  email: string
  password: string
  passwordConfirmation: string
}

function validate(f: Fields): Partial<Record<keyof Fields, string>> {
  const errs: Partial<Record<keyof Fields, string>> = {}
  if (!f.firstName.trim()) errs.firstName = 'Το όνομα είναι υποχρεωτικό'
  if (!f.lastName.trim()) errs.lastName = 'Το επώνυμο είναι υποχρεωτικό'
  if (!f.email) errs.email = 'Το email είναι υποχρεωτικό'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = 'Μη έγκυρο email'
  if (!f.password) errs.password = 'Ο κωδικός είναι υποχρεωτικός'
  else if (f.password.length < 8) errs.password = 'Τουλάχιστον 8 χαρακτήρες'
  if (!f.passwordConfirmation) errs.passwordConfirmation = 'Επιβεβαιώστε τον κωδικό'
  else if (f.password !== f.passwordConfirmation) errs.passwordConfirmation = 'Οι κωδικοί δεν ταιριάζουν'
  return errs
}

export default function SignUp() {
  const { signIn } = useAuth()
  const { t, locale } = useLang()
  const navigate = useNavigate()

  const [fields, setFields] = useState<Fields>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  })
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Fields, string>>>({})
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({})

  const [mutate, { loading }] = useMutation<SignUpData>(SIGN_UP_MUTATION)

  function handleChange(key: keyof Fields, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }))
  }

  function handleBlur(key: keyof Fields) {
    setTouched((prev) => ({ ...prev, [key]: true }))
    const errs = validate({ ...fields })
    setFieldErrors((prev) => ({ ...prev, [key]: errs[key] ?? '' }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errs = validate(fields)
    if (Object.keys(errs).length) {
      setFieldErrors(errs)
      const allTouched: Partial<Record<keyof Fields, boolean>> = {}
      ;(Object.keys(fields) as (keyof Fields)[]).forEach((k) => (allTouched[k] = true))
      setTouched(allTouched)
      return
    }

    setServerErrors([])
    try {
      const { data } = await mutate({
        variables: {
          firstName: fields.firstName,
          lastName: fields.lastName,
          email: fields.email,
          password: fields.password,
          passwordConfirmation: fields.passwordConfirmation,
          locale,
        },
      })

      const result = data?.signUp
      if (!result) return

      if (result.errors.length) {
        setServerErrors(result.errors)
        return
      }

      if (result.token && result.user) {
        await signIn(result.token, result.user)
        navigate('/', { replace: true })
      }
    } catch {
      setServerErrors([t('Κάτι πήγε στραβά. Δοκιμάστε ξανά.', 'Something went wrong. Please try again.')])
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {t('Δημιουργία λογαριασμού', 'Create your account')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t('Βρείτε επαγγελματίες καθαρισμού σε 60 δευτερόλεπτα', 'Find trusted cleaners in 60 seconds')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <FormError errors={serverErrors} />

            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('Όνομα', 'First Name')}
                data-cy="signup-first-name"
                placeholder={locale === 'el' ? 'Μαρία' : 'Maria'}
                value={fields.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                error={touched.firstName ? fieldErrors.firstName : ''}
                required
                autoComplete="given-name"
              />
              <Input
                label={t('Επώνυμο', 'Last Name')}
                data-cy="signup-last-name"
                placeholder={locale === 'el' ? 'Παπαδοπούλου' : 'Papadopoulou'}
                value={fields.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                error={touched.lastName ? fieldErrors.lastName : ''}
                required
                autoComplete="family-name"
              />
            </div>

            <Input
              type="email"
              label="Email"
              data-cy="signup-email"
              placeholder="you@example.com"
              value={fields.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              error={touched.email ? fieldErrors.email : ''}
              required
              autoComplete="email"
            />

            <Input
              type="password"
              label={t('Κωδικός πρόσβασης', 'Password')}
              data-cy="signup-password"
              placeholder="••••••••"
              value={fields.password}
              onChange={(e) => handleChange('password', e.target.value)}
              onBlur={() => handleBlur('password')}
              error={touched.password ? fieldErrors.password : ''}
              hint={t('Τουλάχιστον 8 χαρακτήρες', 'At least 8 characters')}
              required
              autoComplete="new-password"
            />

            <Input
              type="password"
              label={t('Επιβεβαίωση κωδικού', 'Confirm Password')}
              data-cy="signup-password-confirmation"
              placeholder="••••••••"
              value={fields.passwordConfirmation}
              onChange={(e) => handleChange('passwordConfirmation', e.target.value)}
              onBlur={() => handleBlur('passwordConfirmation')}
              error={touched.passwordConfirmation ? fieldErrors.passwordConfirmation : ''}
              required
              autoComplete="new-password"
            />

            <Button type="submit" loading={loading} fullWidth size="lg" data-cy="signup-submit">
              {t('Δημιουργία λογαριασμού', 'Create Account')}
            </Button>

            <p className="text-xs text-slate-400 text-center">
              {t(
                'Με την εγγραφή σας αποδέχεστε τους Όρους Χρήσης και την Πολιτική Απορρήτου.',
                'By signing up you agree to our Terms of Service and Privacy Policy.'
              )}
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          {t('Έχετε ήδη λογαριασμό;', 'Already have an account?')}{' '}
          <Link to="/signin" className="text-primary-600 font-medium hover:underline">
            {t('Σύνδεση', 'Sign In')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-slate-500">
          {t('Είστε επαγγελματίας καθαρισμού;', 'A cleaning professional?')}{' '}
          <Link to="/provider/signup" className="text-secondary-600 font-medium hover:underline">
            {t('Εγγραφή ως επαγγελματίας', 'Register as a provider')}
          </Link>
        </p>
      </div>
    </div>
  )
}
